import React, { useState, useEffect, useCallback } from 'react';
import { docsApi } from '../api';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DocumentDetailModal from '../components/documents/DocumentDetailModal';
import DocumentModal from '../components/documents/DocumentModal';
import { toast } from 'react-toastify';

function timeAgo(iso) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30)  return `${days} days ago`;
  if (days < 365) return `${Math.floor(days/30)} months ago`;
  return `${Math.floor(days/365)} years ago`;
}

function ScoreBar({ score, maxScore }) {
  const pct = maxScore > 0 ? Math.min((score / maxScore) * 100, 100) : 0;
  return (
    <div className="score-bar-track">
      <div className="score-bar-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function CleanupPage() {
  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [limit,     setLimit]     = useState(20);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [viewDoc,   setViewDoc]   = useState(null);
  const [editDoc,   setEditDoc]   = useState(null);

  const load = useCallback(async (lim) => {
    setLoading(true);
    try {
      const { data } = await docsApi.leastImportant(lim);
      setDocs(data);
    } catch {
      toast.error('Failed to load cleanup suggestions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(limit); }, [load, limit]);

  const handleDelete = async () => {
    try {
      await docsApi.delete(deleteDoc.id);
      toast.success('Document deleted');
      setDeleteDoc(null);
      setDocs(d => d.filter(x => x.id !== deleteDoc.id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const maxScore = Math.max(...docs.map(d => d.importanceScore), 1);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">⚡ Cleanup Suggestions</h1>
          <p className="page-subtitle">Documents ranked by least importance — stale and rarely accessed appear first</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Show top</label>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value))}
            style={{
              background: 'var(--input-bg)', border: '1px solid var(--navy-border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
              padding: '0.4rem 0.75rem', fontFamily: 'inherit', fontSize: '0.875rem', outline: 'none',
            }}
          >
            {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Algorithm explainer */}
      {/* <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--navy-border)',
        borderRadius: 'var(--radius)', padding: '1rem 1.25rem', marginBottom: '1.5rem',
        display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: '1.25rem' }}>💡</span>
        <div>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>How the score works</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            <span className="mono" style={{ background: 'var(--accent-soft)', color: 'var(--accent)', padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.75rem' }}>
              score = 0.7 × daysSinceLastAccess + 0.3 / log(hitCount + 2)
            </span>
            <span style={{ marginLeft: '0.75rem' }}>Higher score = less important. Safe to delete.</span>
          </div>
        </div>
      </div> */}

      {loading ? (
        <div className="loading-overlay"><span className="spinner" /></div>
      ) : docs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✨</div>
          <div className="empty-state-title">All caught up!</div>
          <div className="empty-state-text">No cleanup suggestions — your library looks healthy</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {docs.map((doc, i) => (
            <div
              key={doc.id}
              style={{
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                borderRadius: 'var(--radius)', padding: '1rem 1.25rem',
                display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap',
              }}
            >
              {/* Rank badge */}
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: i < 3 ? 'rgba(239,68,68,0.15)' : 'var(--accent-soft)',
                color: i < 3 ? 'var(--danger)' : 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700,
              }}>
                {i + 1}
              </div>

              {/* Doc info */}
              <div style={{ flex: 1, minWidth: 180 }}>
                <div
                  style={{ fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', color: 'var(--text-primary)', marginBottom: '0.2rem' }}
                  onClick={() => setViewDoc(doc)}
                >
                  {doc.title}
                </div>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  <span>👁 {doc.hitCount} views</span>
                  <span>🕐 Last accessed {timeAgo(doc.lastAccessedAt)}</span>
                </div>
                {doc.tags?.length > 0 && (
                  <div className="tags-row" style={{ marginTop: '0.4rem' }}>
                    {doc.tags.map(t => <span key={t} className="tag-badge" style={{ fontSize: '0.68rem' }}>{t}</span>)}
                  </div>
                )}
              </div>

              {/* Score */}
              <div style={{ width: 140, flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>
                  <span>Score</span>
                  <span className="mono">{doc.importanceScore.toFixed(2)}</span>
                </div>
                <ScoreBar score={doc.importanceScore} maxScore={maxScore} />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button className="btn-ghost" style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }} onClick={() => setEditDoc(doc)}>✎</button>
                <button className="btn-danger-ghost" onClick={() => setDeleteDoc(doc)}>🗑 Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteDoc && (
        <ConfirmDialog
          title="Delete Document"
          message={`"${deleteDoc.title}" will be permanently deleted. This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteDoc(null)}
        />
      )}
      {viewDoc && (
        <DocumentDetailModal
          doc={viewDoc}
          onClose={() => setViewDoc(null)}
          onEdit={d => { setViewDoc(null); setEditDoc(d); }}
        />
      )}
      {editDoc && (
        <DocumentModal doc={editDoc} onClose={() => setEditDoc(null)} onSaved={() => load(limit)} />
      )}
    </>
  );
}
